import React, { useCallback } from "react";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { useZoom } from "@rapid/contexts/ZoomContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { DropdownMenu } from "@rapid/components/DropdownMenu";
import { AnnotationLabelsToggle } from "@rapid/components/flow/build/AnnotationLabelsToggle";
import { ZoomControls } from "@rapid/components/flow/build/ZoomControls";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useCurrentMedia, useInferenceQueueItem, InferenceStatus } from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useModel } from "@rapid/contexts/ModelContext";
import { useDownloadAnnotatedFile } from "@rapid/hooks/useDownloadAnnotatedFile";
import { useShareWorkflow } from "@rapid/hooks/useShareWorkflow";
import { useSignInAndCreateWorkflow } from "@rapid/hooks/useSignInAndCreateWorkflow";
import { useVisibilityThroughWorkspace } from "@rapid/hooks/useVisibilityThroughWorkspace";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { BuildSteps, Flows } from "@rapid/types/flows";
import Button from "@scripts/react/components/Button";

interface AnnotationControlsProps {
    className?: string;
}

type ControlOption = {
    id: string;
    label: string;
    action: () => void;
    isSelected?: boolean;
    icon: string;
    disabled?: boolean;
    tooltip?: string;
};

export const AnnotationControls: React.FC<AnnotationControlsProps> = ({ className = "" }) => {
    const { showLabels, setShowLabels } = useCoreApp();
    const { resetZoom, zoomIn, zoomOut } = useZoom();
    const { flow, step } = useFlowState();

    // Model-specific functionality (only for DEPLOY flow)
    const isDeployFlow = flow === Flows.DEPLOY;
    const isBuildReviewStep = flow === Flows.BUILD && step === BuildSteps.REVIEW;
    const showDownloadControl = isDeployFlow || isBuildReviewStep;
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const currentMedia = useCurrentMedia();
    const { task } = useFlow();
    const { isDownloading, isTestingNewFiles, downloadProgress } = useModel();
    const inferenceQueueItem = useInferenceQueueItem(currentMedia?.file?.id);
    const isInferenceComplete = inferenceQueueItem?.status === InferenceStatus.COMPLETED;
    const handleDownload = useDownloadAnnotatedFile({ task });
    const { copied, handleShare } = useShareWorkflow();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const hasWorkflow = !!workflow;
    const downloadDisabled =
        isDownloading ||
        isTestingNewFiles ||
        !isInferenceComplete ||
        (isDeployFlow && signInLoading);

    const handleDownloadClick = useCallback(() => {
        if (isBuildReviewStep || hasWorkflow) {
            handleDownload();
        } else {
            signInAndCreateWorkflow();
        }
    }, [hasWorkflow, handleDownload, isBuildReviewStep, signInAndCreateWorkflow]);

    const handleShareClick = useCallback(() => {
        if (hasWorkflow) {
            handleShare();
        } else {
            signInAndCreateWorkflow();
        }
    }, [hasWorkflow, handleShare, signInAndCreateWorkflow]);

    const getDownloadButtonTooltip = () => {
        if (isDownloading) {
            return `Downloading... ${Math.round(downloadProgress)}%`;
        }
        if (isTestingNewFiles) {
            return "Testing new files...";
        }
        if (isDeployFlow && signInLoading) {
            return "Waiting for sign in...";
        }
        if (!isInferenceComplete) {
            return "Waiting for inference to complete...";
        }
        return `Download annotated ${currentMedia?.file?.isVideo ? "video" : "image"}`;
    };

    const visibility = useVisibilityThroughWorkspace();

    const baseOptions: ControlOption[] = [
        { id: "zoom-in", label: "Zoom In", action: zoomIn, icon: "fas fa-plus" },
        { id: "zoom-out", label: "Zoom Out", action: zoomOut, icon: "fas fa-minus" },
        { id: "reset-zoom", label: "Reset Zoom", action: resetZoom, icon: "fas fa-undo" },
        {
            id: "labels",
            label: showLabels ? "Labels Shown" : "Labels Hidden",
            action: () => setShowLabels(!showLabels),
            icon: showLabels ? "fas fa-eye" : "fas fa-eye-slash"
        }
    ];

    const deployOptions: ControlOption[] = [
        ...(isDeployFlow && visibility === "public"
            ? [
                  {
                      id: "share",
                      label: copied ? "Link Copied!" : "Share Model",
                      action: handleShareClick,
                      icon: copied ? "far fa-check" : "far fa-link",
                      disabled: signInLoading,
                      tooltip: signInLoading ? "Waiting for sign in..." : undefined
                  }
              ]
            : []),
        {
            id: "download",
            label: isDownloading
                ? `Downloading... ${Math.round(downloadProgress)}%`
                : currentMedia?.file?.isVideo
                  ? "Download Video"
                  : "Download Image",
            action: handleDownloadClick,
            icon: isDownloading ? "far fa-spinner fa-spin" : "far fa-download",
            disabled: downloadDisabled,
            tooltip: downloadDisabled ? getDownloadButtonTooltip() : undefined
        }
    ];

    const options: ControlOption[] = showDownloadControl
        ? [...deployOptions, ...baseOptions]
        : baseOptions;

    return (
        <>
            {/* Desktop: Full controls */}
            <div
                className={twClassNames("hidden items-center justify-end gap-2 lg:flex", className)}
            >
                {showDownloadControl && (
                    <>
                        <div className="flex items-center">
                            {isDeployFlow && visibility === "public" && (
                                <TooltipContainer
                                    type="titleOnly"
                                    title={
                                        signInLoading
                                            ? "Waiting for sign in..."
                                            : "Copy shareable link"
                                    }
                                    customButton={
                                        <span>
                                            <Button
                                                type="ghost"
                                                size="small"
                                                onClick={handleShareClick}
                                                disabled={signInLoading}
                                            >
                                                <i
                                                    className={
                                                        copied ? "far fa-check" : "far fa-link"
                                                    }
                                                />
                                                {copied ? "Copied!" : "Share"}
                                            </Button>
                                        </span>
                                    }
                                />
                            )}
                            <TooltipContainer
                                type="titleOnly"
                                title={getDownloadButtonTooltip()}
                                customButton={
                                    <span>
                                        <Button
                                            type="ghost"
                                            size="small"
                                            shape="square"
                                            onClick={
                                                downloadDisabled ? undefined : handleDownloadClick
                                            }
                                            disabled={downloadDisabled}
                                        >
                                            <i
                                                className={
                                                    isDownloading
                                                        ? "fas fa-spinner fa-spin"
                                                        : "far fa-download"
                                                }
                                            />
                                        </Button>
                                    </span>
                                }
                            />
                        </div>
                        <div className="h-6 w-px flex-shrink-0 bg-divider" />
                    </>
                )}

                <div className="flex items-center">
                    <AnnotationLabelsToggle showLabels={showLabels} onToggle={setShowLabels} />
                    <ZoomControls />
                </div>
            </div>

            <div className={twClassNames("flex items-center justify-end lg:hidden", className)}>
                <DropdownMenu
                    items={options}
                    selectedItem={options[0]}
                    onSelectItem={(item) => item.action()}
                    getItemLabel={(item) => item.label}
                    getLeftContent={(item) => <i className={`${item.icon} fa-fw`} />}
                    getItemKey={(item) => item.id}
                    getItemDisabled={(item) => !!item.disabled}
                    getItemClassName={(item) => (item.disabled ? "opacity-50" : undefined)}
                    getItemTooltip={(item) => item.tooltip}
                    buttonClassName="btn2 ghost small square"
                    customMenuButton={<i className="fas fa-cog"></i>}
                    menuItemsClassName="!w-max right-0 mt-1"
                    stayOpenAfterSelection={true}
                />
            </div>
        </>
    );
};
